import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Info, HelpCircle, ArrowLeft, ArrowRight, Check } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type {
  InformacoesAdicionaisSolicitadas,
  CampoInformacaoAdicional,
  DocumentoAnexado,
} from '../../tipos/tiposCriacaoPeticao';
import { CampoComAnexo } from './CampoComAnexo';

interface PropsComponenteInformacoesAdicionais {
  peticaoId: string;
  informacoesSolicitadas: InformacoesAdicionaisSolicitadas;
  onInformacoesConcluidas: (dados: Record<string, unknown>, documentos?: DocumentoAnexado[]) => void;
  onVoltar: () => void;
  onErro?: (erro: string) => void;
}

const DEBOUNCE_SAVE_MS = 1000;

export function ComponenteInformacoesAdicionais({
  peticaoId,
  informacoesSolicitadas,
  onInformacoesConcluidas,
  onVoltar,
  onErro,
}: PropsComponenteInformacoesAdicionais): JSX.Element {
  // States
  const [valores, setValores] = useState<Record<string, unknown>>({});
  const [erros, setErros] = useState<Record<string, string>>({});
  const [camposTocados, setCamposTocados] = useState<Record<string, boolean>>({});
  const [submetendo, setSubmetendo] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [expandedReasons, setExpandedReasons] = useState<Record<string, boolean>>({});

  // NEW: Document attachments per campo
  const [documentosPorCampo, setDocumentosPorCampo] = useState<Record<string, DocumentoAnexado | null>>({});

  const campos = useMemo(() => (informacoesSolicitadas?.campos ?? []), [informacoesSolicitadas?.campos]);

  const STORAGE_KEY = `rascunho_informacoes_adicionais_${peticaoId}`;

  // Load draft and initial values
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      let draft: Record<string, unknown> | null = null;
      if (raw) draft = JSON.parse(raw) as Record<string, unknown>;

      const initialValues: Record<string, unknown> = {};
      if (draft) Object.assign(initialValues, draft);

      // If informacoesSolicitadas has prefilled values
      campos.forEach((c) => {
        if (c.valor !== undefined && c.valor !== null) {
          initialValues[c.id] = c.valor;
        }
      });

      setValores((prev) => ({ ...initialValues, ...prev }));
    } catch (err) {
      console.error('Falha ao carregar rascunho', err);
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [STORAGE_KEY, campos]);

  // Debounced save
  useEffect(() => {
    const payload = { ...valores };
    const t = setTimeout(() => {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
      } catch (err) {
        console.error('Falha ao salvar rascunho', err);
      }
    }, DEBOUNCE_SAVE_MS);

    return () => clearTimeout(t);
  }, [STORAGE_KEY, valores]);

  // Utility: set a value for a field
  const setValor = useCallback((id: string, value: unknown) => {
    setValores((prev) => ({ ...prev, [id]: value }));
  }, []);

  // Touch field
  const touchField = useCallback((id: string) => {
    setCamposTocados((prev) => ({ ...prev, [id]: true }));
  }, []);

  // Validators
  const validarCampo = useCallback((campo: CampoInformacaoAdicional) => {
    const v = valores[campo.id];
    const doc = documentosPorCampo[campo.id];
    const { tipo, obrigatorio, campo: label } = campo;

    // For text/textarea, allow EITHER text OR document OR both
    if (obrigatorio) {
      if (tipo === 'checkbox') {
        if (!v) return `${label} é obrigatório.`;
      } else if (tipo === 'text' || tipo === 'textarea') {
        // Campo is satisfied if EITHER text is provided OR document is attached
        const hasText = v !== undefined && v !== null && String(v).toString().trim().length > 0;
        const hasDoc = !!doc;
        if (!hasText && !hasDoc) {
          return `${label} é obrigatório (preencha o texto ou anexe um documento).`;
        }
      } else if (v === undefined || v === null || String(v).toString().trim().length === 0) {
        return `${label} é obrigatório.`;
      }
    }

    if (v !== undefined && v !== null && String(v).length > 0) {
      if (tipo === 'number') {
        const n = Number(v);
        if (Number.isNaN(n)) return `${label} deve ser um número válido.`;
      }

      if (tipo === 'date') {
        const d = new Date(String(v));
        if (Number.isNaN(d.getTime())) return `${label} deve ser uma data válida.`;
        // Not in future
        const now = new Date();
        if (d > now) return `${label} não pode ser uma data no futuro.`;
      }

      if (tipo === 'textarea' || tipo === 'text') {
        const s = String(v);
        if (s.length > 5000) return `${label} tem limite de 5000 caracteres.`;
      }

      if (tipo === 'select') {
        // ensure value is one of options if provided
        if (campo.opcoes && campo.opcoes.length > 0) {
          if (!campo.opcoes.includes(String(v))) return `${label} selecionado é inválido.`;
        }
      }
    }

    return null;
  }, [valores, documentosPorCampo]);

  const isValidCampo = useCallback((c: CampoInformacaoAdicional) => {
    const v = valores[c.id];
    const doc = documentosPorCampo[c.id];
    const mensagem = validarCampo(c);
    if (mensagem) return false;
    if (c.obrigatorio) {
      if (c.tipo === 'checkbox') return !!v;
      if (c.tipo === 'text' || c.tipo === 'textarea') {
        const hasText = v !== undefined && v !== null && String(v).trim().length > 0;
        const hasDoc = !!doc;
        return hasText || hasDoc;
      }
      return v !== undefined && v !== null && String(v).trim().length > 0;
    }
    return true;
  }, [validarCampo, valores, documentosPorCampo]);

  // Validate all
  const validarTodos = useCallback(() => {
    const newErros: Record<string, string> = {};
    campos.forEach((c) => {
      const mensagem = validarCampo(c);
      if (mensagem) newErros[c.id] = mensagem;
    });
    setErros(newErros);
    return Object.keys(newErros).length === 0;
  }, [campos, validarCampo]);

  // Field change handlers
  const handleChange = useCallback((campo: CampoInformacaoAdicional, value: unknown) => {
    setValor(campo.id, value);
    // reset error for that field
    setErros((prev) => {
      const copy = { ...prev };
      delete copy[campo.id];
      return copy;
    });
  }, [setValor]);

  const handleBlur = useCallback((campo: CampoInformacaoAdicional) => {
    touchField(campo.id);
    const mensagem = validarCampo(campo);
    setErros((prev) => ({ ...prev, ...(mensagem ? { [campo.id]: mensagem } : {}) }));
  }, [touchField, validarCampo]);

  // Scroll to first invalid field
  const scrollToFirstInvalid = useCallback(() => {
    const keys = Object.keys(erros);
    if (keys.length === 0) return;
    const first = keys[0];
    const el = document.querySelector(`[name="informacao_${first}"]`);
    if (el && typeof (el as HTMLElement).focus === 'function') {
      (el as HTMLElement).focus();
    }
  }, [erros]);

  // Progress: count filled fields
  const totalCampos = campos.length;
  const camposObrigatorios = campos.filter((c) => c.obrigatorio).length;
  const preenchidos = campos.filter((c) => {
    const v = valores[c.id];
    if (c.tipo === 'checkbox') return !!v;
    return v !== undefined && v !== null && String(v).trim().length > 0;
  }).length;

  // Handle Continue
  const handleContinuar = useCallback(() => {
    setSubmetendo(true);
    try {
      const valido = validarTodos();
      if (!valido) {
        setSubmetendo(false);
        if (onErro) onErro('Existem erros no formulário. Corrija antes de continuar.');
        scrollToFirstInvalid();
        return;
      }

      // Prepare payload as record fieldId->value
      const payload: Record<string, unknown> = {};
      campos.forEach((c) => {
        payload[c.id] = valores[c.id] !== undefined ? valores[c.id] : c.valor ?? null;
      });

      // Extract document IDs
      const documentosAnexados = Object.values(documentosPorCampo).filter((d): d is DocumentoAnexado => d !== null);

      // Clear draft
      localStorage.removeItem(STORAGE_KEY);

      onInformacoesConcluidas(payload, documentosAnexados);
      setSubmetendo(false);
    } catch (err) {
      console.error(err);
      const mensagem = 'Falha ao submeter informações adicionais.';
      if (onErro) onErro(mensagem);
      setSubmetendo(false);
    }
  }, [validarTodos, onInformacoesConcluidas, campos, valores, onErro, STORAGE_KEY, scrollToFirstInvalid, documentosPorCampo]);

  const handleVoltar = useCallback(() => {
    // Save draft immediately
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(valores));
    } catch (err) {
      console.error('Falha ao salvar rascunho antes de voltar', err);
    }
    onVoltar();
  }, [STORAGE_KEY, valores, onVoltar]);

  // Toggle reason expand
  const toggleReason = useCallback((id: string) => {
    setExpandedReasons((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  // Load more handling for > 10 fields
  const SHOW_BATCH = 5;
  const [visibleCount, setVisibleCount] = useState<number>(SHOW_BATCH);
  useEffect(() => {
    setVisibleCount(SHOW_BATCH);
  }, [informacoesSolicitadas]);

  const handleLoadMore = () => {
    setLoadingMore(true);
    setTimeout(() => {
      setVisibleCount((v) => Math.min(campos.length, v + SHOW_BATCH));
      setLoadingMore(false);
    }, 200);
  };

  // Render field
  function renderField(c: CampoInformacaoAdicional) {
    const idName = `informacao_${c.id}`;
    const valor = valores[c.id] ?? c.valor ?? (c.tipo === 'checkbox' ? false : '');
    const errorMsg = erros[c.id];
    const touched = camposTocados[c.id];
    const showError = !!errorMsg && (touched || Object.keys(erros).length > 0);

    const inputCommonClass = `w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${errorMsg ? 'border-red-400' : 'border-gray-200'}`;

    if (c.tipo === 'text') {
      return (
        <div className="grid grid-cols-1 gap-2" key={c.id}>
          <label htmlFor={idName} className="mb-1 flex items-center gap-2 font-medium text-gray-700">
            {c.campo}{c.obrigatorio && <span className="text-red-600">*</span>}
            {c.descricao && <span title={String(c.descricao)}><HelpCircle className="size-4 text-gray-400" /></span>}
            {isValidCampo(c) && <span className="ml-2 text-green-600"><Check className="size-4" /></span>}
          </label>
          <CampoComAnexo
            campoId={c.id}
            peticaoId={peticaoId}
            nomeCampo={c.campo}
            documentoAnexado={documentosPorCampo[c.id]}
            onDocumentoAnexado={(doc) => setDocumentosPorCampo(prev => ({ ...prev, [c.id]: doc }))}
            onDocumentoRemovido={() => setDocumentosPorCampo(prev => ({ ...prev, [c.id]: null }))}
            onErro={onErro}
          >
            <input
              id={idName}
              name={idName}
              aria-label={c.campo}
              type="text"
              placeholder="Digite aqui ou anexe um arquivo"
              value={String(valor ?? '')}
              onChange={(e) => handleChange(c, e.target.value)}
              onBlur={() => handleBlur(c)}
              className={inputCommonClass}
              aria-invalid={!!errorMsg}
              aria-describedby={errorMsg ? `${idName}-erro` : undefined}
            />
          </CampoComAnexo>
          {c.descricao && <div className="text-sm text-gray-500">{c.descricao}</div>}
          {showError && <div className="text-sm text-red-600" role="alert" id={`${idName}-erro`}>{errorMsg}</div>}
          {c.motivoSolicitacao && (
            <div className="mt-2">
              <button type="button" onClick={() => toggleReason(c.id)} className="text-sm text-primary-600">Por que precisamos desta informação?</button>
              {expandedReasons[c.id] && <div className="mt-2 bg-gray-50 p-3 text-sm text-gray-600">{c.motivoSolicitacao}</div>}
            </div>
          )}
        </div>
      );
    }

    if (c.tipo === 'textarea') {
      return (
        <div className="grid grid-cols-1 gap-2" key={c.id}>
          <label htmlFor={idName} className="mb-1 flex items-center gap-2 font-medium text-gray-700">
            {c.campo}{c.obrigatorio && <span className="text-red-600">*</span>}
            {c.descricao && <span title={String(c.descricao)}><HelpCircle className="size-4 text-gray-400" /></span>}
            {isValidCampo(c) && <span className="ml-2 text-green-600"><Check className="size-4" /></span>}
          </label>
          <CampoComAnexo
            campoId={c.id}
            peticaoId={peticaoId}
            nomeCampo={c.campo}
            documentoAnexado={documentosPorCampo[c.id]}
            onDocumentoAnexado={(doc) => setDocumentosPorCampo(prev => ({ ...prev, [c.id]: doc }))}
            onDocumentoRemovido={() => setDocumentosPorCampo(prev => ({ ...prev, [c.id]: null }))}
            onErro={onErro}
          >
            <textarea
              id={idName}
              name={idName}
              rows={4}
              placeholder="Digite aqui ou anexe um arquivo"
              value={String(valor ?? '')}
              onChange={(e) => handleChange(c, e.target.value)}
              onBlur={() => handleBlur(c)}
              className={inputCommonClass}
              aria-invalid={!!errorMsg}
              aria-describedby={errorMsg ? `${idName}-erro` : undefined}
            />
          </CampoComAnexo>
          <div className="flex items-center justify-between">
            {c.descricao && <div className="text-sm text-gray-500">{c.descricao}</div>}
            <div className="text-xs text-gray-500">{String(valor ?? '').length}/5000</div>
          </div>
          {showError && <div className="text-sm text-red-600" role="alert" id={`${idName}-erro`}>{errorMsg}</div>}
          {c.motivoSolicitacao && (
            <div className="mt-2">
              <button type="button" onClick={() => toggleReason(c.id)} className="text-sm text-primary-600">Por que precisamos desta informação?</button>
              {expandedReasons[c.id] && <div className="mt-2 bg-gray-50 p-3 text-sm text-gray-600">{c.motivoSolicitacao}</div>}
            </div>
          )}
        </div>
      );
    }

    if (c.tipo === 'number') {
      return (
        <div className="grid grid-cols-1 gap-2" key={c.id}>
          <label htmlFor={idName} className="mb-1 flex items-center gap-2 font-medium text-gray-700">
            {c.campo}{c.obrigatorio && <span className="text-red-600">*</span>}
            {c.descricao && <span title={String(c.descricao)}><HelpCircle className="size-4 text-gray-400" /></span>}
            {isValidCampo(c) && <span className="ml-2 text-green-600"><Check className="size-4" /></span>}
          </label>
          <input
            id={idName}
            name={idName}
            type="number"
            placeholder="0"
            value={valor === undefined || valor === null ? '' : (typeof valor === 'number' ? valor : Number(String(valor)))}
            onChange={(e) => handleChange(c, e.target.value === '' ? '' : Number(e.target.value))}
            onBlur={() => handleBlur(c)}
            className={inputCommonClass}
            aria-invalid={!!errorMsg}
            aria-describedby={errorMsg ? `${idName}-erro` : undefined}
          />
          {c.descricao && <div className="text-sm text-gray-500">{c.descricao}</div>}
          {showError && <div className="text-sm text-red-600" role="alert" id={`${idName}-erro`}>{errorMsg}</div>}
        </div>
      );
    }

    if (c.tipo === 'date') {
      return (
        <div className="grid grid-cols-1 gap-2" key={c.id}>
          <label htmlFor={idName} className="mb-1 flex items-center gap-2 font-medium text-gray-700">
            {c.campo}{c.obrigatorio && <span className="text-red-600">*</span>}
            {c.descricao && <span title={String(c.descricao)}><HelpCircle className="size-4 text-gray-400" /></span>}
            {isValidCampo(c) && <span className="ml-2 text-green-600"><Check className="size-4" /></span>}
          </label>
          <input
            id={idName}
            name={idName}
            type="date"
            value={valor === undefined || valor === null ? '' : String(valor)}
            onChange={(e) => handleChange(c, e.target.value)}
            onBlur={() => handleBlur(c)}
            className={inputCommonClass}
            aria-invalid={!!errorMsg}
            aria-describedby={errorMsg ? `${idName}-erro` : undefined}
          />
          {c.descricao && <div className="text-sm text-gray-500">{c.descricao}</div>}
          {showError && <div className="text-sm text-red-600" role="alert" id={`${idName}-erro`}>{errorMsg}</div>}
        </div>
      );
    }

    if (c.tipo === 'select') {
      return (
        <div className="grid grid-cols-1 gap-2" key={c.id}>
          <label htmlFor={idName} className="mb-1 flex items-center gap-2 font-medium text-gray-700">{c.campo}{c.obrigatorio && <span className="text-red-600">*</span>} {c.descricao && <span title={String(c.descricao)}><HelpCircle className="size-4 text-gray-400" /></span>}</label>
          <select
            id={idName}
            name={idName}
            value={valor === undefined || valor === null ? '' : String(valor)}
            onChange={(e) => handleChange(c, e.target.value)}
            onBlur={() => handleBlur(c)}
            className={inputCommonClass}
            aria-invalid={!!errorMsg}
            aria-describedby={errorMsg ? `${idName}-erro` : undefined}
          >
            <option value="">Selecione...</option>
            {c.opcoes?.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
          {c.descricao && <div className="text-sm text-gray-500">{c.descricao}</div>}
          {showError && <div className="text-sm text-red-600" role="alert" id={`${idName}-erro`}>{errorMsg}</div>}
        </div>
      );
    }

    if (c.tipo === 'checkbox') {
      return (
        <div className="grid grid-cols-1 gap-2" key={c.id}>
          <label className="mb-1 inline-flex items-center gap-2 font-medium text-gray-700">
            <input
              id={idName}
              name={idName}
              type="checkbox"
              checked={!!valor}
              onChange={(e) => handleChange(c, e.target.checked)}
              onBlur={() => handleBlur(c)}
              className="size-4"
            />
            <span>{c.campo}{c.obrigatorio && <span className="text-red-600">*</span>}</span>
          </label>
          {c.descricao && <div className="text-sm text-gray-500">{c.descricao}</div>}
          {showError && <div className="text-sm text-red-600" role="alert" id={`${idName}-erro`}>{errorMsg}</div>}
        </div>
      );
    }

    // fallback
    return (
      <div className="grid grid-cols-1 gap-2" key={c.id}>
        <div className="text-gray-700">Tipo desconhecido: {c.tipo}</div>
      </div>
    );
  }

  // If no fields
  if (totalCampos === 0) {
    return (
      <div className="rounded-lg bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Informações adicionais</h2>
            <p className="text-sm text-gray-600">{informacoesSolicitadas.justificativaGeral ?? 'A IA não solicitou informações adicionais.'}</p>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Info className="size-5" />
            <span>Nenhuma informação adicional necessária</span>
          </div>
        </div>
        <div className="mt-4">
          <div className="mb-4 flex items-center gap-4">
            <div className="rounded-full bg-green-50 p-2 text-green-600"><Check className="size-4" /></div>
            <div className="text-sm">✅ Nenhuma informação adicional necessária!</div>
          </div>
          <div className="flex items-center justify-end">
            <button
              type="button"
              onClick={() => onInformacoesConcluidas({})}
              className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 font-medium text-white transition-colors duration-200 hover:bg-primary-700"
            >
              Continuar
              <ArrowRight className="size-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-white p-6 shadow-sm">
      <div className="space-y-6">

        {/* HEADER */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Informações adicionais</h2>
            {informacoesSolicitadas.justificativaGeral && (
              <div className="mt-1 flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-gray-800">
                <Info className="size-5 text-blue-600" />
                <div className="prose prose-sm max-w-none text-gray-800">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{String(informacoesSolicitadas.justificativaGeral)}</ReactMarkdown>
                </div>
              </div>
            )}
          </div>

          <div className="text-sm text-gray-500">
            <div>{preenchidos}/{totalCampos} preenchidos</div>
            <div className="text-xs">{camposObrigatorios} obrigatórios</div>
          </div>
        </div>

        {/* COUNTER */}
        <div className="flex items-center gap-4">
          <div className="text-sm font-medium text-gray-700">{totalCampos} informações solicitadas</div>
          <div className="text-sm text-gray-500">{camposObrigatorios} obrigatórias • {totalCampos - camposObrigatorios} opcionais</div>
        </div>

        {/* FIELDS */}
        <div className="grid grid-cols-1 gap-4">
          {campos.slice(0, visibleCount).map((c) => renderField(c))}
        </div>

        {totalCampos > visibleCount && (
          <div className="flex items-center justify-center">
            <button type="button" onClick={handleLoadMore} className="text-sm text-primary-600">{loadingMore ? 'Carregando...' : 'Carregar mais'}</button>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={handleVoltar}
            className="rounded-lg bg-gray-100 px-4 py-2 text-gray-700 hover:bg-gray-200"
          >
            <ArrowLeft className="mr-2 inline-block size-4" />
            Voltar
          </button>

          <div className="flex items-center gap-4">
            <div className="text-sm text-gray-500">{preenchidos}/{totalCampos} preenchidos</div>
            <button
              type="button"
              onClick={handleContinuar}
              disabled={submetendo || (camposObrigatorios > 0 && preenchidos < camposObrigatorios) || Object.keys(erros).length > 0}
              className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 font-medium text-white transition-colors duration-200 hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submetendo ? 'Enviando...' : 'Continuar'}
              <ArrowRight className="size-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ComponenteInformacoesAdicionais;
